<template>
  <!--
    Full-screen flex column glued to the visual viewport (see useVisualViewport).
    The whole page does not scroll; only the inner <main> of the chat core does.
    This replaces a `position: fixed` input bar, which iOS WKWebView mispositions
    once the keyboard opens.
  -->
  <div
    class="bg-surface-default-low mood-warm fixed inset-x-0 top-0 flex flex-col overflow-hidden"
    :style="viewportStyle"
  >
    <!-- Webview shell: the core talks directly to the native bridge host. -->
    <ShoppingAssistantChat
      v-if="isWebview"
      :can-resume-latest
      :entry-point
      :initial-chat-id
      :is-seeded-launch
      :page-context
      :seeded-message
    />

    <!-- Standalone web page: framed by the web-page host adapter (RevDialog
         confirmations, close() navigates home instead of dismissing a webview). -->
    <WebPageShoppingAssistantHost
      v-else
      :can-resume-latest
      :entry-point
      :initial-chat-id
      :is-seeded-launch
      :page-context
      :seeded-message
    />
  </div>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, onMounted, onUnmounted, provide } from 'vue'

import { useVisualViewport } from '../../webviews/useVisualViewport'
import WebPageShoppingAssistantHost from '../components/WebPageShoppingAssistantHost.vue'
import { useShoppingAssistantContext } from '../composables/useShoppingAssistantContext'
import { SHOPPING_ASSISTANT_HOST_KEY } from '../composables/useShoppingAssistantHost.injection-key'
import { useWebviewShoppingAssistantHost } from '../composables/useWebviewShoppingAssistantHost'
import {
  SHOPPING_ASSISTANT_NEW,
  SHOPPING_ASSISTANT_NEW_WEBVIEW,
} from '../routes.constants'

import ShoppingAssistantChat from './ShoppingAssistantChat.vue'

const route = useRoute()

// The explicit "new chat" routes always start fresh — never resume the latest
// chat there, even though they carry no chatId and no seed (a route change
// between route records always remounts this component, so this is stable per
// mount).
const canResumeLatest = computed(
  () =>
    route.name !== SHOPPING_ASSISTANT_NEW &&
    route.name !== SHOPPING_ASSISTANT_NEW_WEBVIEW,
)

// The native webview shell derives the chat context from the route (deep-link
// query params, chatId path param) and frames the route-agnostic chat core.
const { pageContext, entryPoint, seededMessage, isSeededLaunch } =
  useShoppingAssistantContext()

const initialChatId =
  typeof route.params.chatId === 'string' ? route.params.chatId : null

// Set by the /webview/assistant routes only. A route change between the
// webview and plain variants always remounts this component (different route
// records), so reading it once here is safe.
const isWebview = computed(() => Boolean(route.meta.isWebview))

// Keeps the chat root sized to the area above the on-screen keyboard.
const { style: viewportStyle } = useVisualViewport()

if (isWebview.value) {
  provide(SHOPPING_ASSISTANT_HOST_KEY, useWebviewShoppingAssistantHost())
}

// This webview page is a full-screen position:fixed layout, so its document
// never scrolls. Lock document-level overscroll while mounted so a swipe that
// starts outside the inner scroller (e.g. on the header or input bar) can't
// rubber-band the WKWebView root scroll view and drag the native modal down on
// iOS. The scope is the page lifecycle only — webview-default is shared with
// pages (help-center, generic webviews) that do rely on document scrolling, so
// we restore the previous value on unmount.
let previousOverscrollBehavior = ''
onMounted(() => {
  previousOverscrollBehavior = document.documentElement.style.overscrollBehavior
  document.documentElement.style.overscrollBehavior = 'none'
})
onUnmounted(() => {
  document.documentElement.style.overscrollBehavior = previousOverscrollBehavior
})
</script>
